import axios from 'axios';
import GatewayAxiosClient from '~/store/services/GatewayAxiosClient';
import getGatewayUri from '~/store/services/getGatewayUri';
import { getToken } from '~/store/session';
import type {
  SimulationServiceV1SimulationCreateData,
  SimulationServiceV1SimulationCreatePayload,
  SimulationServiceV1SimulationUpdatePayload,
  SimulationServiceV1SimulationContextMappingsCreateData,
  SimulationServiceV1SimulationContextMappingsCreatePayload,
  SimulationServiceV1SimulationUploadsUploadTemplateListParams,
  SimulationServiceV1SimulationDetailData,
  SimulationServiceV1SimulationDetailReportListData,
  SimulationServiceV1SimulationListData,
  SimulationServiceV1SimulationExportsListListData,
  SimulationServiceV1SimulationExportsDownloadUrlListData,
  SimulationServiceV1SimulationReportsDetailData,
} from '@shipium-corp/shipium-ts-types';
import { SimulationExecutionStatus } from '~/store/simulation/types';
import type {
  Simulation,
  SimulationUpdate,
  SimulationContextMapping,
  SimulationContextMappingCreate,
  SimulationFulfillmentContextMappingTypes,
  SimulationStatus,
  SimulationDetail,
  SimulationShipmentBatch,
  SimulationEvent,
  SimulationServiceVersion,
  SimulationExportMetadata,
  FetchSimulationReportParams,
  SimulationReport,
} from '~/store/simulation/types';
import { rawResultsListV2, rawResultDetailV2, rawResultsFilterOptionsV2 } from './rawResultsV2';

export const SIMULATION_QUERY_KEY = 'simulation-service/simulation';
export const SIMULATION_DETAIL_QUERY_KEY = 'simulation-service/simulation/detail';

// ---- v1 (internal gateway) ----

const V1 = '/shipium-internal-proxy';
const v1Headers = () => ({ headers: { authorization: `Bearer ${getToken()}` } });

const requireSimulationId = (links: SimulationContextMappingCreate[]): string => {
  const simulationId = links[0]?.simulationId;
  if (!simulationId) {
    throw new Error('Unable to create context mappings. No simulation detected');
  }
  return simulationId;
};

export const simulation = {
  cleanup: async (simulationId: string) => {
    const response = await GatewayAxiosClient.delete<{ deletedAssets: number }>(
      `/partner-management-proxy/api/v1/partners/simulation/cleanup/${simulationId}`,
    );

    return {
      data: response.data,
      invalidateQueryKeys: [['simulation-service/simulation'], ['simulation-service/simulation/id', simulationId]],
    };
  },
  createV1: async (sim: Partial<Simulation>) => {
    const { data } = await GatewayAxiosClient.post<Simulation>(`${V1}/simulation`, sim, v1Headers());
    return { data, invalidateQueryKeys: [[SIMULATION_QUERY_KEY]] };
  },
  updateV1: async (sim: SimulationUpdate) => {
    const { data } = await GatewayAxiosClient.put<Simulation>(`${V1}/simulation/${sim.simulationId}`, sim, v1Headers());
    return {
      data,
      invalidateQueryKeys: [
        [SIMULATION_QUERY_KEY, sim.simulationId],
        [SIMULATION_DETAIL_QUERY_KEY, sim.simulationId],
      ],
    };
  },
  contextMappingsV1: async (links: SimulationContextMappingCreate[]) => {
    const simulationId = requireSimulationId(links);
    const { data } = await GatewayAxiosClient.post<SimulationContextMapping[]>(
      `${V1}/simulation/simulation-context-mapping/${simulationId}`,
      links,
      v1Headers(),
    );
    return { data, invalidateQueryKeys: [[SIMULATION_DETAIL_QUERY_KEY, simulationId]] };
  },
  // Payload is built by the caller (useStartSimulation): getPayloadForSimulationType lives in
  // store/simulation/utils, which pulls in guided-step component modules — the service layer must
  // not import component code (a circular ~/services import breaks unrelated page specs).
  startV1: async (sim: Simulation, simulationAssetCreatePayload: unknown) => {
    const { data } = await GatewayAxiosClient.post<Simulation>(
      `${V1}/simulation/${sim.simulationId}/start-simulation`,
      { simulationAssetCreatePayload },
      v1Headers(),
    );
    return {
      data,
      invalidateQueryKeys: [
        [SIMULATION_QUERY_KEY, sim.simulationId],
        [SIMULATION_DETAIL_QUERY_KEY, sim.simulationId],
      ],
    };
  },
};

// ---- v2 (simulation-service); adapters map the generated wire types to legacy domain types ----

export const V2_BASE = '/simulation-proxy/api/v1';

// create/update/get/start all share one response shape.
type V2SimulationWire = SimulationServiceV1SimulationCreateData;

// Normalize the strict/nullable v2 response to the non-null legacy Simulation.
// v2 dropped simulationStartDate/simulationEndDate — supply '' to hold the existing type.
export function adaptV2Simulation(v2: V2SimulationWire): Simulation {
  return {
    simulationId: v2.simulationId,
    partnerId: v2.partnerId,
    name: v2.name,
    description: v2.description ?? '',
    simulationType: v2.simulationType as Simulation['simulationType'],
    executionPriority: v2.executionPriority,
    executionStatus: (v2.executionStatus as SimulationExecutionStatus) ?? SimulationExecutionStatus.PENDING,
    simulationStatus: v2.simulationStatus as SimulationStatus,
    includesHistoricalSelection: v2.includesHistoricalSelection,
    simulationStartDate: '',
    simulationEndDate: '',
    simulationSourceStartDate: v2.simulationSourceStartDate ?? '',
    simulationSourceEndDate: v2.simulationSourceEndDate ?? '',
    samplingPercentage: v2.samplingPercentage ?? undefined,
    historicalShipmentS3Locations: v2.historicalShipmentS3Locations ?? null,
    createdBy: v2.createdBy,
    lastUpdatedBy: v2.lastUpdatedBy,
    creationTimestamp: v2.creationTimestamp,
    lastUpdatedTimestamp: v2.lastUpdatedTimestamp,
    simulationShipmentOptions: (v2.simulationShipmentOptions ?? undefined) as Simulation['simulationShipmentOptions'],
    simulationCostingOptions: (v2.simulationCostingOptions ?? undefined) as Simulation['simulationCostingOptions'],
    simulationTntOptions: (v2.simulationTntOptions ?? undefined) as Simulation['simulationTntOptions'],
    simulationSetupData: (v2.simulationSetupData ?? undefined) as Simulation['simulationSetupData'],
    visibleToPartner: v2.visibleToPartner ?? undefined,
    namespaceId: v2.namespaceId ?? null,
  };
}

async function createSimulationV2(sim: Partial<Simulation>) {
  // drop the stray top-level simulationId the create step sends as ''
  const { simulationId: _drop, ...body } = sim as Partial<Simulation> & { simulationId?: string };
  const { data } = await GatewayAxiosClient.post<V2SimulationWire>(
    `${V2_BASE}/simulation`,
    body as SimulationServiceV1SimulationCreatePayload,
  );
  return {
    data: adaptV2Simulation(data),
    invalidateQueryKeys: [['simulation-service/v2/simulation']],
  };
}

async function updateSimulationV2(sim: SimulationUpdate) {
  const { simulationId, ...body } = sim;
  const { data } = await GatewayAxiosClient.put<V2SimulationWire>(
    `${V2_BASE}/simulation/${simulationId}`,
    body as SimulationServiceV1SimulationUpdatePayload,
  );
  return {
    data: adaptV2Simulation(data),
    invalidateQueryKeys: [
      ['simulation-service/v2/simulation'],
      ['simulation-service/v2/simulation/detail', simulationId],
    ],
  };
}

export type V2ContextMappingRow = SimulationServiceV1SimulationContextMappingsCreateData[number];

export function adaptV2ContextMapping(v2: V2ContextMappingRow): SimulationContextMapping {
  return {
    mappingKey: v2.mappingKey,
    simulationId: v2.simulationId,
    mappingValue: v2.mappingValue,
    mappingType: v2.mappingType as SimulationFulfillmentContextMappingTypes,
    simulationFulfillmentContextId: v2.simulationFulfillmentContextId ?? '',
    createdBy: v2.createdBy,
    lastUpdatedBy: v2.lastUpdatedBy,
    creationTimestamp: v2.creationTimestamp ?? '',
    lastUpdatedTimestamp: v2.lastUpdatedTimestamp ?? '',
  };
}

async function createContextMappingsV2(args: { simulationId: string; mappings: SimulationContextMappingCreate[] }) {
  const { simulationId, mappings } = args;
  // v2 takes simulationId from the path, not per-link
  const body = mappings.map((m) => ({
    mappingType: m.mappingType,
    mappingValue: m.mappingValue,
    simulationFulfillmentContextId: m.simulationFulfillmentContextId,
  })) as SimulationServiceV1SimulationContextMappingsCreatePayload;
  const { data } = await GatewayAxiosClient.post<SimulationServiceV1SimulationContextMappingsCreateData>(
    `${V2_BASE}/simulation/${simulationId}/context-mappings`,
    body,
  );
  return {
    data: data.map(adaptV2ContextMapping),
    invalidateQueryKeys: [['simulation-service/v2/simulation/detail', simulationId]],
  };
}

// Body-less; 422 if there are no context mappings or the sim isn't startable. Shares the create response shape.
async function startSimulationV2(simulationId: string) {
  const { data } = await GatewayAxiosClient.post<V2SimulationWire>(
    `${V2_BASE}/simulation/${simulationId}/start-simulation`,
  );
  return {
    data: adaptV2Simulation(data),
    invalidateQueryKeys: [
      ['simulation-service/v2/simulation'],
      ['simulation-service/v2/simulation/detail', simulationId],
    ],
  };
}

// Exact in-window row count for the selected uploads, PRE-sampling. Dates are both-or-neither
// (one bound → 400); omit both for the whole-file count. 400 also for unknown/unvalidated uploads.
export type SimulationShipmentCountParams = { uploadIds: string[]; startDate?: string; endDate?: string };
const shipmentCountV2 = {
  queryKey: 'simulation-service/v2/shipment-count',
  configurationType: 'short-cache' as const,
  fn: async (params: SimulationShipmentCountParams): Promise<number> => {
    const { data } = await GatewayAxiosClient.post<{ count: number }>(`${V2_BASE}/simulation/shipment-count`, params);
    return data.count;
  },
};

// v2 streams raw CSV bytes (no { downloadUrl } wrapper); the caller saves the Blob.
const uploadTemplateV2 = {
  queryKey: 'simulation-service/v2/upload-template',
  configurationType: 'cached' as const,
  fn: async (includeAllFields: boolean): Promise<Blob> => {
    const params: SimulationServiceV1SimulationUploadsUploadTemplateListParams = { includeAllFields };
    const response = await GatewayAxiosClient.get<Blob>(`${V2_BASE}/simulation/uploads/upload-template`, {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

export type V2DetailReport = SimulationServiceV1SimulationDetailReportListData;
export type V2BatchRow = V2DetailReport['batches']['rows'][number];
type V2EventRow = V2DetailReport['events'][number];

function adaptV2Batch(row: V2BatchRow): SimulationShipmentBatch {
  // v2 dropped legacy year/month/day/hour (partitioning changed) and omits createdBy; the Shipments
  // tab that reads those is disabled for v2, so placeholder them. Rework is a later plan.
  return {
    simulationBatchId: row.simulationBatchId,
    simulationId: row.simulationId,
    batchS3Location: row.batchS3Location,
    batchStatus: row.batchStatus,
    responseS3Location: row.responseS3Location ?? '',
    totalRows: row.totalRows ?? 0,
    successRows: row.successRows ?? 0,
    errorRows: row.errorRows ?? 0,
    skippedRows: row.skippedRows ?? 0,
    year: 0,
    month: 0,
    day: 0,
    hour: 0,
    createdBy: '',
    lastUpdatedBy: row.lastUpdatedBy,
    creationTimestamp: row.creationTimestamp,
    lastUpdatedTimestamp: row.lastUpdatedTimestamp,
    performanceMetrics: (row.performanceMetrics ?? {}) as SimulationShipmentBatch['performanceMetrics'],
  };
}

function adaptV2Event(e: V2EventRow): SimulationEvent {
  return {
    simulationEventId: e.simulationEventId,
    simulationId: e.simulationId,
    simulationShipmentBatchId: e.simulationShipmentBatchId ?? undefined,
    simulationEventType: e.simulationEventType,
    simulationEventDetail: e.simulationEventDetail,
    simulationEventMetaData: e.simulationEventMetaData,
    eventCreatedBy: e.eventCreatedBy,
    eventTimestamp: e.eventTimestamp,
  };
}

// Merge the bare record + detail-report into the flat legacy SimulationDetail; hoist batch totals.
export function adaptV2DetailReport(bare: V2SimulationWire, report: V2DetailReport): SimulationDetail {
  const base = adaptV2Simulation(bare);
  const { totals } = report.batches;
  return {
    ...base,
    simulationSetupData: base.simulationSetupData ?? {},
    simulationShipmentBatches: report.batches.rows.map(adaptV2Batch),
    simulationContextMappings: report.contextMappings.map(adaptV2ContextMapping),
    simulationEvents: report.events.map(adaptV2Event),
    expectedBatches: totals.expectedBatches,
    finalBatchStatusCount: totals.finalBatchStatusCount,
    totalShipmentCount: totals.totalShipmentCount,
    completedShipmentCount: totals.completedShipmentCount,
    errorShipmentCount: totals.errorShipmentCount,
    skippedShipmentCount: totals.skippedShipmentCount,
    failedShipmentCount: totals.failedShipmentCount,
    cancelledShipmentCount: totals.cancelledShipmentCount,
  };
}

// v2 split the detail read into bare record + detail-report; merge to the legacy shape.
// no-cache: resume/version resolution need a fresh read and the report changes as batches process.
const simulationDetailV2 = {
  queryKey: 'simulation-service/v2/simulation/detail',
  configurationType: 'no-cache' as const,
  fn: async (simulationId: string): Promise<SimulationDetail> => {
    const [bare, report] = await Promise.all([
      GatewayAxiosClient.get<SimulationServiceV1SimulationDetailData>(`${V2_BASE}/simulation/${simulationId}`),
      GatewayAxiosClient.get<SimulationServiceV1SimulationDetailReportListData>(
        `${V2_BASE}/simulation/${simulationId}/detail-report`,
      ),
    ]);
    return adaptV2DetailReport(bare.data as V2SimulationWire, report.data);
  },
};

// Landing list (bare array; no status whitelist — the caller filters). queryKey matches the
// invalidateQueryKeys the v2 create/update/start mutations emit.
const simulationListV2 = {
  queryKey: 'simulation-service/v2/simulation',
  configurationType: 'default' as const,
  fn: async (): Promise<Simulation[]> => {
    const { data } = await GatewayAxiosClient.get<SimulationServiceV1SimulationListData>(`${V2_BASE}/simulation`);
    return data.map(adaptV2Simulation);
  },
};

// Export listing — v2 wraps the rows in an { exports } envelope; unwrap to the legacy array.
const exportsListV2 = {
  queryKey: 'simulation-service/exports',
  configurationType: 'default' as const,
  fn: async (simulationId: string): Promise<SimulationExportMetadata[]> => {
    const { data } = await GatewayAxiosClient.get<SimulationServiceV1SimulationExportsListListData>(
      `${V2_BASE}/simulation/${simulationId}/exports-list`,
    );
    return data.exports;
  },
};

// Signed download URL for one export file (imperative — used from a click handler, not a query).
async function exportDownloadUrlV2(simulationId: string, file: string): Promise<string> {
  const { data } = await GatewayAxiosClient.get<SimulationServiceV1SimulationExportsDownloadUrlListData>(
    `${V2_BASE}/simulation/${simulationId}/exports/${encodeURIComponent(file)}/download-url`,
  );
  return data.downloadUrl;
}

// v1 twin of the above (kept beside it for parity); imperative, used from a click handler.
export async function exportDownloadUrlV1(simulationId: string, file: string): Promise<string> {
  const res = await axios.get<{ downloadUrl: string }>(
    `${getGatewayUri()}${V1}/simulation/${simulationId}/get-signed-download-url/${file}`,
    { headers: { authorization: `Bearer ${getToken()}` }, withCredentials: true },
  );
  return res.data.downloadUrl;
}

// Report read. v2 wraps reporting-filters rows in { reportingFilters: [...] } — unwrap to the bare
// array the consumers reduce over. (The routing hook keeps un-ported types on v1.)
const reportV2 = {
  queryKey: 'simulation-service/report',
  configurationType: 'cached' as const,
  fn: async (params: FetchSimulationReportParams): Promise<SimulationReport> => {
    const { simulationId, reportType, originSlug, dayOfWeek, tenantId, tntType, carrier } = params;
    const { data } = await GatewayAxiosClient.get<SimulationServiceV1SimulationReportsDetailData>(
      `${V2_BASE}/simulation/${simulationId}/reports/${reportType}`,
      {
        params: {
          ...(originSlug ? { originSlug } : {}),
          ...(dayOfWeek ? { dayOfWeek } : {}),
          ...(tenantId ? { tenantId } : {}),
          ...(tntType ? { tntType } : {}),
          ...(carrier ? { carrier } : {}),
        },
      },
    );
    const report: SimulationReport = data;
    if (reportType === 'reporting-filters') {
      return { ...report, reportData: report.reportData?.reportingFilters ?? [] };
    }
    return report;
  },
};

// Probe v2 GET /:id; a 404 means the sim exists only in the v1 store. Used when there's no stamp.
const versionProbe = {
  queryKey: 'simulation-service/version-probe' as const,
  configurationType: 'cached' as const,
  fn: async (simulationId: string): Promise<SimulationServiceVersion> => {
    try {
      await GatewayAxiosClient.get(`${V2_BASE}/simulation/${simulationId}`);
      return 'v2';
    } catch (error) {
      const status = axios.isAxiosError(error)
        ? error.response?.status
        : (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        return 'v1';
      }
      throw error;
    }
  },
};

// v2 (simulation-service) call surface, mirroring the v1 `simulation` object above. Members drop
// the V2 suffix since the namespace already conveys the version. Adapters/types stay standalone.
export const simulationV2 = {
  create: createSimulationV2,
  update: updateSimulationV2,
  createContextMappings: createContextMappingsV2,
  start: startSimulationV2,
  shipmentCount: shipmentCountV2,
  uploadTemplate: uploadTemplateV2,
  detail: simulationDetailV2,
  versionProbe,
  list: simulationListV2,
  exportsList: exportsListV2,
  exportDownloadUrl: exportDownloadUrlV2,
  report: reportV2,
  rawResultsList: rawResultsListV2,
  rawResultDetail: rawResultDetailV2,
  rawResultsFilterOptions: rawResultsFilterOptionsV2,
};
